var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-510 — the "Mapa" segment of the ontology browser.
 *
 * <p>Owns everything the segment needs (class picker, verdict banner, legend,
 * diagram, the two gap lists) so `SemanticCatalogBrowser` — already 1300 lines — gains
 * a render branch rather than a fifth screen's worth of state.
 *
 * <p><b>What it will not do is claim a green verdict it did not earn.</b> When no
 * template could be read the fields render `unknown`, the gap lists stay empty and the
 * banner says the form side is missing. A map that quietly showed "0 nálezů" on a
 * failed fetch would be the check that cannot fail, and this screen exists precisely
 * to be a check.
 *
 * <p>The caveat under the diagram is not boilerplate either: the comparison names the
 * templates it actually read. "Chybí ve formuláři" means missing from those, and the
 * reader can see which they were.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Select, MessageBanner, Headline, Loading } from '@folio/stripes/components';
import OntologyMapDiagram from './OntologyMapDiagram';
import HelpTooltip from '../components/HelpTooltip';
import useOntologyFormMapSources from '../hooks/useOntologyFormMapSources';
import { FIELD_FORM_STATUSES, buildOntologyFormMap, fieldFormStatusColor, } from './ontologyFormMap';
var KEY = 'ui-czna-authorities.semanticCatalog.map';
var swatch = function (color) { return ({
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    backgroundColor: color,
    marginRight: '5px',
}); };
var sectionStyle = {
    border: '1px solid #dfe6ec',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    marginTop: '0.75rem',
};
var OntologyMapView = function (_a) {
    var _b;
    var catalog = _a.catalog, className = _a.className, onSelectClass = _a.onSelectClass, onOpenProperty = _a.onOpenProperty, onOpenField = _a.onOpenField;
    var intl = useIntl();
    var t = function (id, values) { return intl.formatMessage({ id: "".concat(KEY, ".").concat(id) }, values); };
    var _c = useOntologyFormMapSources(catalog, className), forms = _c.forms, recordTypes = _c.recordTypes, availableRecordTypes = _c.availableRecordTypes, isLoading = _c.isLoading, isPartial = _c.isPartial;
    var classOptions = Object.entries((_b = catalog === null || catalog === void 0 ? void 0 : catalog.classes) !== null && _b !== void 0 ? _b : {})
        .map(function (_a) {
        var _b, _c;
        var name = _a[0], def = _a[1];
        return ({ value: name, label: "".concat((_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : name, " (").concat(name, ")") });
    })
        .sort(function (a, b) { return a.label.localeCompare(b.label, 'cs'); });
    var map = className
        ? buildOntologyFormMap({ catalog: catalog, className: className, forms: forms, availableRecordTypes: availableRecordTypes })
        : undefined;
    var picker = (React.createElement(Select, { id: "ontology-map-class", label: t('classPicker'), name: "ontologyMapClass", value: className !== null && className !== void 0 ? className : '', dataOptions: __spreadArray([{ value: '', label: t('classPicker.none') }], classOptions, true), onChange: function (event) { return onSelectClass(event.target.value); } }));
    if (!className || !map) {
        return (React.createElement("div", { "data-testid": "ontology-map-view" },
            picker,
            React.createElement("p", null, t('pickAClass'))));
    }
    var legend = (React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '0.9rem', fontSize: '0.8rem' } },
        FIELD_FORM_STATUSES.map(function (status) { return (React.createElement("span", { key: status, "data-testid": "ontology-map-legend-".concat(status) },
            React.createElement("span", { style: swatch(fieldFormStatusColor(status)) }),
            t("status.".concat(status)),
            ' ',
            React.createElement("strong", null, map.counts[status]))); }),
        React.createElement(HelpTooltip, { messageId: "".concat(KEY, ".help.status"), testKey: "mapStatus", idPrefix: "ontology-map-help" })));
    return (React.createElement("div", { "data-testid": "ontology-map-view" },
        picker,
        !map.formsResolved && !isLoading && (React.createElement("div", { "data-testid": "ontology-map-forms-unresolved" },
            React.createElement(MessageBanner, { type: "warning" }, recordTypes.length === 0
                ? t('noRecordTypes', { class: map.classLabel })
                : t('formsUnread', { types: recordTypes.join(', ') })))),
        isPartial && map.formsResolved && (React.createElement("div", { "data-testid": "ontology-map-partial" },
            React.createElement(MessageBanner, { type: "warning" }, t('partial')))),
        map.plannedRecordTypes.length > 0 && (React.createElement("div", { "data-testid": "ontology-map-planned" },
            React.createElement(MessageBanner, { type: "default" }, t('plannedTypes', { types: map.plannedRecordTypes.join(', ') })))),
        map.typesNotJudgedInReverse.length > 0 && (React.createElement("div", { "data-testid": "ontology-map-not-judged" },
            React.createElement(MessageBanner, { type: "default" }, t('typesNotJudged', { types: map.typesNotJudgedInReverse.join(', ') })))),
        isLoading ? React.createElement(Loading, null) : (React.createElement(React.Fragment, null,
            React.createElement("div", { style: { margin: '0.5rem 0' } }, legend),
            React.createElement(OntologyMapDiagram, { map: map, onOpenProperty: onOpenProperty, onOpenField: onOpenField }),
            React.createElement("p", { style: { fontSize: '0.8rem', color: '#5a6b7d' }, "data-testid": "ontology-map-basis" }, map.templates.length > 0
                ? t('comparedAgainst', {
                    count: map.templates.length, templates: map.templates.join(', '),
                })
                : t('comparedAgainstNothing')),
            React.createElement("div", { style: sectionStyle, "data-testid": "ontology-map-reverse-fields" },
                React.createElement(Headline, { size: "small", margin: "none" },
                    React.createElement(FormattedMessage, { id: "".concat(KEY, ".reverseFields.title") }),
                    ' ',
                    React.createElement(HelpTooltip, { messageId: "".concat(KEY, ".help.reverseFields"), testKey: "mapReverseFields", idPrefix: "ontology-map-help" })),
                map.reverseFields.length === 0 ? (React.createElement("p", { "data-testid": "ontology-map-reverse-fields-".concat(map.formsResolved ? 'none' : 'unknown') }, map.formsResolved ? t('reverseFields.none') : t('reverseFields.unknown'))) : (React.createElement("ul", null, map.reverseFields.map(function (gap) { return (React.createElement("li", { key: gap.tag, "data-testid": "ontology-map-reverse-field-".concat(gap.tag) },
                    React.createElement("strong", null, gap.tag),
                    gap.label ? " \u2014 ".concat(gap.label) : '',
                    ' · ',
                    gap.recordTypes.join(', '),
                    !gap.knownToOntology && " \u00B7 ".concat(t('reverseFields.unknownTag')))); })))),
            React.createElement("div", { style: sectionStyle, "data-testid": "ontology-map-unknown-subfields" },
                React.createElement(Headline, { size: "small", margin: "none" },
                    React.createElement(FormattedMessage, { id: "".concat(KEY, ".unknownSubfields.title") }),
                    ' ',
                    React.createElement(HelpTooltip, { messageId: "".concat(KEY, ".help.unknownSubfields"), testKey: "mapUnknownSubfields", idPrefix: "ontology-map-help" })),
                map.unknownSubfields.length === 0 ? (React.createElement("p", { "data-testid": "ontology-map-unknown-subfields-".concat(map.formsResolved ? 'none' : 'unknown') }, map.formsResolved ? t('unknownSubfields.none') : t('unknownSubfields.unknown'))) : (React.createElement("ul", null, map.unknownSubfields.map(function (gap) { return (React.createElement("li", { key: gap.tag, "data-testid": "ontology-map-unknown-subfield-".concat(gap.tag) },
                    React.createElement("strong", null, gap.tag),
                    ' ',
                    gap.codes.map(function (code) { return "$".concat(code); }).join(' '),
                    ' · ',
                    gap.recordTypes.join(', '))); })))),
            React.createElement("div", { style: sectionStyle, "data-testid": "ontology-map-unbound-subfields" },
                React.createElement(Headline, { size: "small", margin: "none" },
                    React.createElement(FormattedMessage, { id: "".concat(KEY, ".unboundSubfields.title") }),
                    ' ',
                    React.createElement(HelpTooltip, { messageId: "".concat(KEY, ".help.unboundSubfields"), testKey: "mapUnboundSubfields", idPrefix: "ontology-map-help" })),
                React.createElement("p", { "data-testid": "ontology-map-unbound-subfields-count" }, map.formsResolved
                    ? t('unboundSubfields.count', {
                        tags: map.unboundSubfields.length,
                        codes: map.unboundSubfields.reduce(function (n, gap) { return n + gap.codes.length; }, 0),
                    })
                    : t('unboundSubfields.unknown')))))));
};
export default OntologyMapView;
